import React, { useCallback } from 'react';

import {
  Autocomplete,
  TextField,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import _ from 'lodash';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import type { TranslateFunction } from 'react-admin';

interface CallPropertyInputProps {
  callProperty: string | CallPropertyOptions;
  label: string;
  customInputLabel: string;
  checkboxLabel: string;
  onChange: (callProperty: string) => void;
}

const callPropertyOptionsArray = [
  'called_id_number',
  'called_id_name',
  'caller_id_number',
  'caller_id_name',
  'remote_network_addr',
] as const;

const customPropertySuggestions = ['orld_ivr_selection'];

export type CallPropertyOptions = (typeof callPropertyOptionsArray)[number];
export const callPropertyOptions = callPropertyOptionsArray as unknown as CallPropertyOptions[];

export const translateCallProperty = (translate: TranslateFunction, callProperty: string): string => {
  return translate(`callflows.components.callPropertyInput.options.${callProperty}`, { _: callProperty });
};

export const callPropertyValidation = (callProperty: string): string => {
  const reg = /^[a-z][a-z0-9_][a-z0-9_]+$/;
  if (callProperty === '') {
    flowchartLogger.debug('Validation: Call property is empty');
    return 'callflows.nodes.router.callPropertyErrorEmpty';
  } else if (!reg.test(callProperty)) {
    flowchartLogger.debug('Validation: Call property is invalid', callProperty);
    return 'callflows.nodes.router.callPropertyErrorInvalid';
  } else {
    flowchartLogger.debug('Validation: Call property is valid', callProperty);
    return '';
  }
};

const CallPropertyInput = ({
  callProperty,
  label,
  customInputLabel,
  checkboxLabel,
  onChange,
}: CallPropertyInputProps): React.ReactElement => {
  const [callpropertyHelperText, setCallPropertyHelperText] = React.useState<string>('');
  const [isCustomRouter, setIsCustomRouter] = React.useState<boolean>(
    callProperty !== null && !callPropertyOptions.includes(callProperty as CallPropertyOptions)
  );
  const translate = useTranslate();

  const handleCallPropertyValidation = useCallback((): void => {
    setCallPropertyHelperText(translate(callPropertyValidation(callProperty), { label }));
  }, [callProperty, translate, label]);

  const handleCheckboxChange = useCallback((): void => {
    const newCustomRouterValue = !isCustomRouter;
    setIsCustomRouter(newCustomRouterValue);
    if (newCustomRouterValue) {
      flowchartLogger.debug('Custom router enabled, calling the validation function for callProperty:', callProperty);
      onChange(callProperty);
      handleCallPropertyValidation();
    } else {
      flowchartLogger.debug('Custom router disabled');
      onChange('called_id_number');
    }
  }, [callProperty, handleCallPropertyValidation, isCustomRouter, onChange]);

  // Validate the call property on mount
  React.useEffect(() => {
    handleCallPropertyValidation();
  }, [handleCallPropertyValidation]);

  return (
    <Box data-testid='call-property-input'>
      <FormControl margin='none' sx={{ minWidth: 120, display: 'block' }} size='small' variant='outlined'>
        {isCustomRouter ? (
          <Box display='flex' alignItems='start' flexDirection='column'>
            <Autocomplete
              id={`callproperty-${_.kebabCase(customInputLabel)}`}
              value={callProperty}
              freeSolo
              options={customPropertySuggestions}
              sx={{ width: 300 }}
              onInputChange={(_event, value) => {
                onChange(value);
                handleCallPropertyValidation();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={customInputLabel}
                  slotProps={{ htmlInput: { ...params.inputProps, pattern: '^[a-z][a-z0-9_][a-z0-9_]+$' } }}
                  error={callpropertyHelperText !== ''}
                  helperText={callpropertyHelperText}
                />
              )}
            />
          </Box>
        ) : (
          <>
            <InputLabel id={`callproperty-${_.kebabCase(label)}`}>{label}</InputLabel>
            <Select
              required
              labelId={`callproperty-${_.kebabCase(label)}`}
              value={
                callProperty !== null && callPropertyOptions.includes(callProperty as CallPropertyOptions)
                  ? callProperty
                  : callPropertyOptions[0]
              }
              label={label}
              onChange={(event) => {
                onChange(event.target.value);
              }}
            >
              {callPropertyOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {translateCallProperty(translate, option)}
                </MenuItem>
              ))}
            </Select>
          </>
        )}
      </FormControl>
      <FormControl margin='none'>
        <FormControlLabel
          control={<Checkbox checked={isCustomRouter} onChange={handleCheckboxChange} />}
          label={checkboxLabel}
        />
      </FormControl>
    </Box>
  );
};

export default CallPropertyInput;
